import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from '../../../../../../context/Toast';
import {
  useUpdateExpenseMutation,
  UpdateExpenseInput,
} from '../../../../__generated__/graphql';
import { collectFormDataExtraFields } from '../../../../components/ExtraFields/logic';
import { isSalesforceId } from '../sharedMerchantHelpers';
import { mapToExtraFieldInput } from '../../../../components/ExtraFields/extraFieldInternalValue';

export type BaseFormData = {
  description: string;
  expenseGroupId: string;
  projectId: string;
  categoryId: string;
  [key: string]: any;
};

const EXTRA_FIELD_TO_UPDATE_EXPENSE_INPUT_KEY: Record<string, string> = {
  flair__Date_From__c: 'dateFrom',
  flair__Date_To__c: 'dateTo',
  flair__Time_From__c: 'timeFrom',
  flair__Time_To__c: 'timeTo',
  flair__Description__c: 'description',
  flair__Merchant__c: 'merchantId',
  flair__Expense_Category__c: 'category',
  flair__Project__c: 'project',
  flair__Expense_Group__c: 'expenseGroupId',
};

const extractExpenseDate = <T extends BaseFormData>(formData: T): string => {
  let dateField: Date | null = getDateField(formData);
  if (dateField) {
    return dateField.toISOString().split('T')[0];
  }
  return new Date().toISOString().split('T')[0];
};

const getDateField = <T extends BaseFormData>(formData: T): Date | null => {
  if ('dateFrom' in formData && formData.dateFrom instanceof Date) {
    return formData.dateFrom;
  }
  if ('expenseDate' in formData && formData.expenseDate instanceof Date) {
    return formData.expenseDate;
  }
  if ('fromDate' in formData && formData.fromDate instanceof Date) {
    return formData.fromDate;
  }
  return null;
};

const buildBaseUpdateInput = <T extends BaseFormData>(
  expenseId: string,
  formData: T,
  expenseDate: string,
  additionalFields?: Record<string, any>,
): Record<string, any> => {
  return {
    id: expenseId,
    description: formData.description || null,
    dateFrom: expenseDate,
    expenseGroupId:
      formData.expenseGroupId === '' ? null : formData.expenseGroupId,
    project: formData.projectId === '' ? null : formData.projectId,
    category: formData.categoryId === '' ? null : formData.categoryId,
    ...additionalFields,
  };
};

const addMerchantToUpdateInput = <T extends BaseFormData>(
  updateInput: Record<string, any>,
  formData: T,
): void => {
  if (!('merchantId' in formData)) {
    return;
  }

  const merchantIdValue =
    formData.merchantId === '' || !formData.merchantId
      ? null
      : formData.merchantId;

  if (merchantIdValue && isSalesforceId(merchantIdValue)) {
    updateInput.merchantId = merchantIdValue;
  } else {
    updateInput.merchantId = null;
  }
};

export type UseExpenseFormStateOptions<T extends BaseFormData> = {
  expenseId: string | null;
  initialData: T;
  onFormDataChange?: (data: T) => void;
};

export type UseExpenseFormStateReturn<T extends BaseFormData> = {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  handleInputChange: (field: string, value: any) => void;
  isUpdatingExpense: boolean;
  hasLoadedInitialData: boolean;
  setHasLoadedInitialData: (value: boolean) => void;
  hasDataChanged: (
    currentFormData: T,
    currentExtraFieldsString: string,
  ) => boolean;
  autoSaveExpense: (
    allFormValues: any,
    additionalFields?: Record<string, any>,
  ) => Promise<void>;
  updateExpenseWithMerchant: (merchantId: string) => Promise<void>;
  lastSavedDataRef: React.MutableRefObject<{
    formData: T;
    extraFieldsString: string;
  } | null>;
};

export const useExpenseFormState = <T extends BaseFormData>({
  expenseId,
  initialData,
  onFormDataChange,
}: UseExpenseFormStateOptions<T>): UseExpenseFormStateReturn<T> => {
  const { t } = useTranslation();
  const { addError } = useToasts();
  const [updateExpenseMutation] = useUpdateExpenseMutation();

  const [formData, setFormData] = useState<T>(initialData);
  const [isUpdatingExpense, setIsUpdatingExpense] = useState<boolean>(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] =
    useState<boolean>(false);

  const lastSavedDataRef = useRef<{
    formData: T;
    extraFieldsString: string;
  } | null>(null);

  const hasDataChanged = useCallback(
    (currentFormData: T, currentExtraFieldsString: string): boolean => {
      if (!lastSavedDataRef.current) {
        return false;
      }

      const last = lastSavedDataRef.current;

      if (currentExtraFieldsString !== last.extraFieldsString) return true;

      const formKeys = Object.keys(currentFormData) as (keyof T)[];

      for (const key of formKeys) {
        const currentValue = currentFormData[key];
        const lastValue = last.formData[key];

        const isDate = (val: any): val is Date => val instanceof Date;

        if (isDate(currentValue) || isDate(lastValue)) {
          const currentDateStr = isDate(currentValue)
            ? currentValue.toISOString()
            : null;
          const lastDateStr = isDate(lastValue)
            ? lastValue.toISOString()
            : null;
          if (currentDateStr !== lastDateStr) return true;
        } else {
          const normalizedCurrent = currentValue === '' ? null : currentValue;
          const normalizedLast = lastValue === '' ? null : lastValue;
          if (normalizedCurrent !== normalizedLast) return true;
        }
      }

      return false;
    },
    [],
  );

  const handleInputChange = useCallback((field: string, value: any) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      return newData;
    });
  }, []);

  const autoSaveExpense = useCallback(
    async (allFormValues: any, additionalFields?: Record<string, any>) => {
      if (!expenseId) {
        return;
      }

      if (
        'merchantId' in formData &&
        formData.merchantId &&
        !isSalesforceId(formData.merchantId)
      ) {
        return;
      }

      const currentExtraFields = collectFormDataExtraFields(allFormValues);
      const currentExtraFieldsString = JSON.stringify(currentExtraFields);

      if (!hasDataChanged(formData, currentExtraFieldsString)) {
        return;
      }

      try {
        setIsUpdatingExpense(true);

        const expenseDate = extractExpenseDate(formData);
        const updateInput = buildBaseUpdateInput(
          expenseId,
          formData,
          expenseDate,
          additionalFields,
        );

        addMerchantToUpdateInput(updateInput, formData);

        const extraFieldsToSend = currentExtraFields.filter((field) => {
          const typedInputKey =
            EXTRA_FIELD_TO_UPDATE_EXPENSE_INPUT_KEY[field.fieldApiName];
          return !typedInputKey || !(typedInputKey in updateInput);
        });

        if (extraFieldsToSend.length > 0) {
          updateInput.extraFields = extraFieldsToSend.map(mapToExtraFieldInput);
        }

        await updateExpenseMutation({
          variables: {
            input: updateInput as UpdateExpenseInput,
          },
        });

        lastSavedDataRef.current = {
          formData,
          extraFieldsString: currentExtraFieldsString,
        };
      } catch (err) {
        console.error('Error auto-saving expense:', err);
        addError(t('expenses.perDiemForm.expenseUpdateFailed'));
      } finally {
        setIsUpdatingExpense(false);
      }
    },
    [expenseId, formData, hasDataChanged, updateExpenseMutation, addError, t],
  );

  const updateExpenseWithMerchant = useCallback(
    async (merchantId: string) => {
      if (!expenseId) {
        return;
      }

      try {
        setIsUpdatingExpense(true);

        const expenseDate = extractExpenseDate(formData);
        const updateInput = buildBaseUpdateInput(
          expenseId,
          formData,
          expenseDate,
        );

        updateInput.merchantId = merchantId;

        await updateExpenseMutation({
          variables: {
            input: updateInput as UpdateExpenseInput,
          },
        });

        const updatedFormData = {
          ...formData,
          merchantId,
        };

        setFormData(updatedFormData);

        const currentExtraFields = collectFormDataExtraFields({});
        const currentExtraFieldsString = JSON.stringify(currentExtraFields);

        lastSavedDataRef.current = {
          formData: updatedFormData,
          extraFieldsString: currentExtraFieldsString,
        };
      } catch (err) {
        console.error('Error updating expense with merchant:', err);
        addError(t('expenses.perDiemForm.expenseUpdateFailed'));
      } finally {
        setIsUpdatingExpense(false);
      }
    },
    [expenseId, formData, updateExpenseMutation, addError, t],
  );

  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange(formData);
    }
  }, [formData, onFormDataChange]);

  return {
    formData,
    setFormData,
    handleInputChange,
    isUpdatingExpense,
    hasLoadedInitialData,
    setHasLoadedInitialData,
    hasDataChanged,
    autoSaveExpense,
    updateExpenseWithMerchant,
    lastSavedDataRef,
  };
};
